import { Typography, Box } from "@mui/material";
import { useNavigate } from "react-router-dom";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import PersonIcon from "@mui/icons-material/Person";
import GroupIcon from "@mui/icons-material/Group";
import BusinessCenterOutlinedIcon from "@mui/icons-material/BusinessCenterOutlined";
import EngineeringOutlinedIcon from "@mui/icons-material/EngineeringOutlined";
import FindInPageOutlinedIcon from "@mui/icons-material/FindInPageOutlined";
import CreditCardOutlinedIcon from "@mui/icons-material/CreditCardOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import BarChartOutlinedIcon from "@mui/icons-material/BarChartOutlined";
import GppGoodOutlinedIcon from "@mui/icons-material/GppGoodOutlined";
import QuestionAnswerOutlinedIcon from "@mui/icons-material/QuestionAnswerOutlined";
import { useCallback, useMemo } from "react";
import { useAppSelector } from "../../store/hooks";
import { RootState } from "../../store/store";
import {
  HUB_COLORS,
  HUB_CENTER_SIZE,
  HUB_CENTER_URL,
  HUB_NODE_CIRCLE_SIZE,
  HUB_NODE_ICON_SIZE,
  HUB_NODE_LABEL_FONT_SIZE,
  HUB_NODE_LABEL_MAX_WIDTH,
  HUB_NODES,
  HUB_SIZE,
  HubNodeConfig,
  INNER_RING_RADIUS,
  OUTER_RING_RADIUS,
} from "../hubConfig";

const CENTER = HUB_SIZE / 2;

function polarPosition(angleDeg: number, radius: number) {
  const rad = (angleDeg * Math.PI) / 180;
  return {
    left: CENTER + radius * Math.sin(rad),
    top: CENTER - radius * Math.cos(rad),
  };
}

const LABEL_BELOW = {
  top: "100%",
  left: "50%",
  transform: "translateX(-50%)",
  mt: 0.75,
} as const;

function HubIcon({ iconKey, color, size }: { iconKey: string; color: string; size: number }) {
  const sx = { color, fontSize: size };
  switch (iconKey) {
    case "customer":
      return <PersonIcon sx={sx} />;
    case "admin":
      return <ManageAccountsIcon sx={sx} />;
    case "agent":
      return <GroupIcon sx={sx} />;
    case "submission portal":
      return <BusinessCenterOutlinedIcon sx={sx} />;
    case "workers comp":
      return <EngineeringOutlinedIcon sx={sx} />;
    case "ask":
      return <QuestionAnswerOutlinedIcon sx={sx} />;
    default:
      return null;
  }
}

function HubNode({
  node,
  onNavigate,
}: {
  node: HubNodeConfig;
  onNavigate: (node: HubNodeConfig) => void;
}) {
  const radius = node.ring === "inner" ? INNER_RING_RADIUS : OUTER_RING_RADIUS;
  const { left, top } = polarPosition(node.angle, radius);
  const isInner = node.ring === "inner";
  const clickable = !node.disabled && !!node.path;
  return (
    <Box
      sx={{
        position: "absolute",
        left,
        top,
        width: HUB_NODE_CIRCLE_SIZE,
        height: HUB_NODE_CIRCLE_SIZE,
        transform: "translate(-50%, -50%)",
        cursor: clickable ? "pointer" : "default",
        zIndex: isInner ? 6 : 3,
        "&:hover": clickable
          ? {
              "& .hub-node-circle": {
                transform: "scale(1.06)",
              },
            }
          : undefined,
      }}
      id={`${node.title}-button`}
      data-test-id={`test-${node.title}-button`}
      aria-label={node.title}
      onClick={() => clickable && onNavigate(node)}
    >
      <Box
        className="hub-node-circle"
        sx={{
          width: "100%",
          height: "100%",
          borderRadius: "50%",
          backgroundColor: node.bgColor,
          border: "2px solid rgba(255, 255, 255, 0.85)",
          boxShadow: "0 2px 10px rgba(26, 35, 126, 0.14)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          transition: "transform 0.2s ease",
          opacity: !isInner && node.disabled ? 0.82 : 1,
        }}
      >
        <HubIcon iconKey={node.iconKey} color={node.iconColor} size={HUB_NODE_ICON_SIZE} />
      </Box>
      <Typography
        sx={{
          position: "absolute",
          ...LABEL_BELOW,
          color: HUB_COLORS.label,
          fontWeight: 600,
          fontSize: HUB_NODE_LABEL_FONT_SIZE,
          lineHeight: 1.2,
          textAlign: "center",
          maxWidth: HUB_NODE_LABEL_MAX_WIDTH,
          whiteSpace: "nowrap",
          pointerEvents: "none",
        }}
      >
        {node.title}
      </Typography>
    </Box>
  );
}

export default function HeroSection() {
  const navigate = useNavigate();
  const { uiSettings } = useAppSelector((state: RootState) => state.uiSettings);

  const { innerNodes, outerNodes } = useMemo(
    () => ({
      innerNodes: HUB_NODES.filter((n) => n.ring === "inner"),
      outerNodes: HUB_NODES.filter((n) => n.ring === "outer"),
    }),
    [],
  );

  const handleNavigate = useCallback(
    (node: HubNodeConfig) => {
      if (!node.path) return;
      if (node.external) {
        window.open(node.path, "_blank");
        return;
      }
      navigate(node.path);
      if (node.iconKey !== "customer") {
        window.history.pushState(null, "", "/");
      }
    },
    [navigate],
  );

  return (
    <Box
      sx={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
      }}
    >
      <Box
        id="hub-center-button"
        data-test-id="test-hub-center-button"
        aria-label="Smart IMS Xymphony"
        onClick={() => window.open(HUB_CENTER_URL, "_blank")}
        sx={{
          position: "absolute",
          left: CENTER,
          top: CENTER,
          transform: "translate(-50%, -50%)",
          width: HUB_CENTER_SIZE,
          height: HUB_CENTER_SIZE,
          borderRadius: "50%",
          backgroundColor: "#ffffff",
          boxShadow: HUB_COLORS.hubShadow,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 2,
          gap: 0.25,
          cursor: "pointer",
          transition: "transform 0.2s ease, box-shadow 0.2s ease",
          "&:hover": {
            transform: "translate(-50%, -50%) scale(1.04)",
            boxShadow: "0 10px 36px rgba(26, 35, 126, 0.18)",
          },
        }}
      >
        {uiSettings?.logo ? (
          <Box
            component="img"
            src={`data:image/png;base64,${uiSettings.logo}`}
            alt="Logo"
            sx={{
              maxWidth: "85%",
              maxHeight: "85%",
              objectFit: "contain",
            }}
          />
        ) : (
          <>
            <Typography
              sx={{
                color: HUB_COLORS.hubSubtitle,
                fontSize: "0.75rem",
                fontWeight: 400,
                letterSpacing: "0.02em",
              }}
            >
              Smart IMS
            </Typography>
            <Typography
              sx={{
                color: HUB_COLORS.hubTitle,
                fontSize: "1.5rem",
                fontWeight: 700,
                lineHeight: 1.1,
              }}
            >
              Xymphony
            </Typography>
          </>
        )}
      </Box>

      {outerNodes.map((node) => (
        <HubNode key={node.title} node={node} onNavigate={handleNavigate} />
      ))}
      {innerNodes.map((node) => (
        <HubNode key={node.title} node={node} onNavigate={handleNavigate} />
      ))}
    </Box>
  );
}
